import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { Link } from "react-router-dom";

const API_URL = "https://sunrisemeditechsystems.com/api"; // Define your backend API URL

export default function Cart() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stockMap, setStockMap] = useState({}); // { productId: { saleStock, rentalStock } }

  useEffect(() => {
    loadCartItems();
  }, []);

  useEffect(() => {
    // Fetch stock for all cart items
    const fetchStocks = async () => {
      const ids = cartItems.map(item => item.productId);
      if (ids.length === 0) return;
      try {
        const responses = await Promise.all(ids.map(id => axios.get(`${API_URL}/products/${id}`)));
        const map = {};
        responses.forEach(res => {
          map[res.data.id] = { saleStock: res.data.saleStock, rentalStock: res.data.rentalStock };
        });
        setStockMap(map);
      } catch (err) {
        // ignore
      }
    };
    fetchStocks();
  }, [cartItems]);

  const loadCartItems = () => {
    const userId = localStorage.getItem("currentUserId");
    const cartKey = `cart_${userId}`;
    const items = JSON.parse(localStorage.getItem(cartKey) || "[]");
    setCartItems(items);
    calculateTotal(items);
  };

  const calculateTotal = (items) => {
    const total = items.reduce((sum, item) => {
      return sum + item.price * item.quantity;
    }, 0);
    setTotal(total);
  };

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const item = cartItems[index];
    const stock = stockMap[item.productId] || {};
    const maxStock = item.type === 'buy' ? stock.saleStock : stock.rentalStock;
    if (maxStock !== undefined && newQuantity > maxStock) {
      Swal.fire('Error', 'Not enough stock available.', 'error');
      return;
    }
    const updatedItems = [...cartItems];
    updatedItems[index].quantity = newQuantity;
    setCartItems(updatedItems);
    const userId = localStorage.getItem("currentUserId");
    const cartKey = `cart_${userId}`;
    localStorage.setItem(cartKey, JSON.stringify(updatedItems));
    calculateTotal(updatedItems);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleRemoveItem = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this item from your cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedItems = cartItems.filter((_, i) => i !== index);
        setCartItems(updatedItems);
        const userId = localStorage.getItem("currentUserId");
        const cartKey = `cart_${userId}`;
        localStorage.setItem(cartKey, JSON.stringify(updatedItems));
        calculateTotal(updatedItems);      
        Swal.fire("Removed!", "Item has been removed from your cart.", "success");
        window.dispatchEvent(new Event('cartUpdated'));
      }
    });
  };

  const handleCheckout = () => {
    // Check for out of stock items
    const outOfStock = cartItems.some(item => {
      const stock = stockMap[item.productId] || {};
      const maxStock = item.type === 'buy' ? stock.saleStock : stock.rentalStock;
      return !maxStock || item.quantity > maxStock;
    });
    if (outOfStock) {
      Swal.fire('Error', 'One or more items are out of stock. Please update your cart.', 'error');
      return;
    }
    const token = localStorage.getItem("userToken");
    if (!token) {
      Swal.fire("Login Required", "Please login to proceed to checkout.", "info")
      return;
    }
    navigate("/checkout");
  };

  return (
    <>
      {/* BREADCRUMB AREA START */}
      <div className="ltn__breadcrumb-area text-left bg-overlay-white-30 bg-image" data-bs-bg="img/bg/sunrise-medical.jpg">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12" style={{ padding: "0 6%" }}>
              <div className="ltn__breadcrumb-inner">
                <h1 className="page-title">Shopping Cart</h1>
                <div className="ltn__breadcrumb-list">
                  <ul>
                      <li>
                      <Link to="/" className="bg-black-color">
                          <span className="ltn__secondary-color">
                            <i className="fas fa-home bg-black-color" />
                          </span>{" "}
                          Home
                      </Link>
                      </li>
                    <li>Shopping Cart</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* BREADCRUMB AREA END */}

      {/* SHOPPING CART AREA START */}
      <div className="liton__shopping-cart-area mb-120">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-9" style={{ padding: "0 6%" }}>
              <div className="shoping-cart-inner">
                <div className="shoping-cart-table table-responsive">
                  <table className="table" style={{ tableLayout: 'auto', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: "10%" }}></th>
                        <th style={{ width: "20%" }}>Image</th>
                        <th style={{ width: "20%" }}>Product</th>
                        <th style={{ width: "20%" }}>Price</th>
                        <th style={{ width: "20%" }}>Quantity</th>
                        <th style={{ width: "20%" }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">Your cart is empty.</td>
                        </tr>
                      ) : (
                        cartItems.map((item, index) => {
                          const stock = stockMap[item.productId] || {};
                          const maxStock = item.type === 'buy' ? stock.saleStock : stock.rentalStock;
                          const isOutOfStock = !maxStock || maxStock <= 0;
                          return (
                            <tr key={index} style={{ height: 140 }}>
                              <td style={{ width: "3%" }}>
                                <Link to="#" onClick={() => handleRemoveItem(index)}>
                                  <i className="fas fa-times" />
                                </Link>
                              </td>
                              <td style={{ width: "20%" }}>
                                <Link to={`/product/${item.productId}`}>
                                  <img
                                    src={`https://sunrisemeditechsystems.com${item.image}`}
                                    alt={item.name}
                                    style={{
                                      maxWidth: 160,
                                      height: 120,
                                      objectFit: 'contain',
                                      borderRadius: 8,
                                      border: '1px solid #eee',
                                      background: '#fff',
                                      display: 'block'
                                    }}
                                  />
                                </Link>
                              </td>
                              <td style={{ fontWeight: 600, fontSize: 18, width: "18%", textAlign: "center" }}>
                                <Link to={`/product/${item.productId}`}>{item.name}</Link>
                                <small className="d-block">{item.type === "buy" ? "(Sale)" : "(Rent)"}</small>
                              </td>
                              <td style={{ fontSize: 16, width: "20%", textAlign: "center" }}>
                                ₹{item.price.toFixed(2)}
                              </td>
                              <td style={{ width: "25%" }}>
                                <div style={styles.container}>
                                  <button
                                    type="button"
                                    style={styles.button}                                   
                                    onClick={() => handleQuantityChange(index, item.quantity - 1)}
                                    disabled={item.quantity <= 1}
                                  >
                                    −
                                  </button>

                                  <input
                                    type="text"
                                    style={styles.input}
                                    value={item.quantity}
                                    className="cart-input"
                                    readOnly
                                  />

                                  <button
                                    type="button"
                                    style={{ ...styles.button, color: "#0a9a73" }}
                                    onClick={() => handleQuantityChange(index, item.quantity + 1)}
                                    disabled={isOutOfStock || item.quantity >= maxStock}
                                  >
                                    +
                                  </button>
                                </div>
                                <div style={{ fontSize: '0.95em', color: '#888', marginTop: 4, marginLeft: "30%" }}>
                                  {isOutOfStock
                                    ? <span style={{ color: 'red' }}>Out of Stock</span>
                                    : `In Stock: ${maxStock}`}
                                </div>
                              </td >
                              <td style={{ fontWeight: 600, fontSize: 16, width: "20%", textAlign: "center" }}>
                                ₹{(item.price * item.quantity).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="shoping-cart-total ltn__secondary-bg">
                  <h2 className="border-bottom-0">Cart Totals</h2>
                  <table className="table">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Cart Subtotal</strong>
                        </td>
                        <td>₹{total.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Shipping</strong>
                        </td>
                        <td>Free Shipping</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Order Total</strong>
                        </td>
                        <td>
                          <strong>₹{total.toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="btn-wrapper" style={{
                    display: 'flex',
                    gap: '0',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '20px'
                  }}>
                    <Link
                      to="/shop"
                      className="theme-btn-1 btn btn-effect-1"
                      style={{
                        flex: 1,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        textAlign: 'center'
                      }}
                    >
                      Continue Shopping
                    </Link>
                    <button
                      className="theme-btn-2 btn btn-effect-2"
                      style={{
                        flex: 1,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        textAlign: 'center'
                      }}
                      onClick={handleCheckout}
                      disabled={cartItems.some(item => { const stock = stockMap[item.productId] || {}; const maxStock = item.type === 'buy' ? stock.saleStock : stock.rentalStock; return !maxStock || item.quantity > maxStock; })}
                    >
                      Checkout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* SHOPPING CART AREA END */}
    </>
  );
}

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    borderRadius: "5px",
    background: "rgb(248, 249, 250)",
    border: " 1px solid gray",
    overflow: "hidden",
    width: "60%",
    textAlign: "center",
    marginLeft: "58px"
  },
  button: {

    fontSize: "20px",
    width: "40px",
    height: "40px",
    background: "rgb(248, 248, 248)",
    cursor: "pointer",
    transition: "background 0.2s",
    border: " 1px solid gray"
  },
  input: {
    textAlign: "center",
    border: "none",
    outline: "none",
    fontSize: "16px",
    background: "transparent",
    padding: "5px 20px 4px 21px"
  }

};
